import assert = require('assert');

import { BigNumber, Contract, providers, Signer } from 'ethers';
import * as TREX from '@tokenysolutions/t-rex';

import { connectAt, getTransactionOptions, loadContractWithCaller, TransactionOptionsInterface } from '../utils/utils';
import {
  approve,
  batchBurn,
  batchForceTransfer,
  batchFreezeAddresses,
  batchFreezePartialTokens,
  batchMint,
  batchTransfer,
  batchUnfreezePartialTokens,
  burn,
  decreaseAllowance,
  forceTransfer,
  freezeAddress,
  freezePartialTokens,
  getAllowance,
  getBalance,
  getDecimals,
  getFrozenAmount,
  getName,
  getOnchainID,
  getPauseStatus,
  getSymbol,
  getVersion,
  increaseAllowance,
  isFrozen,
  mint,
  pauseToken,
  recoveryAddress,
  setName,
  setOnchainID,
  setSymbol,
  transfer,
  transferFrom,
  unfreezePartialTokens,
  unpauseToken,
} from '../contract-methods/token.methods';
import { TokenFactory } from './token-factory';
import { TokenConfigurationInterface } from './token-configuration.interface';
import { Role } from '../utils/enum';
import { addressesFormat, required } from '../contract-methods/decorators.methods';
import { Registry } from './registry';

type TransactionResponse = providers.TransactionResponse;

export class Token extends Registry {
  /**
   * Create a new TokenFactory that can be used to plan the deployment of a token.
   * @param configuration Configuration describing the token setup.
   */
  static createFactory(configuration: TokenConfigurationInterface): TokenFactory {
    return new TokenFactory(configuration);
  }

  /**
   * Connect to an instance of deployed Token Contract.
   * @param {string} contractAddress
   * @param {Signer | providers.Provider} caller
   * @returns {Promise<Token>}
   */
  static async at(contractAddress: string, caller: Signer | providers.Provider): Promise<Token> {
    assert(Signer.isSigner(caller) || providers.Provider.isProvider(caller));
    const token = new Token();
    if (Signer.isSigner(caller)) {
      token.signer = caller as Signer;
    }
    if (providers.Provider.isProvider(caller)) {
      token.provider = caller as providers.Provider;
    }
    token.token = await connectAt({
      contractAddress: contractAddress,
      abi: TREX.contracts.Token.abi,
      caller,
    });
    await token.loadIdentityRegistry(await token.token.identityRegistry());
    await token.loadCompliance(await token.token.compliance());
    return token;
  }

  /**
   * Returns Name of the token.
   * @param {TransactionOptionsInterface} caller
   * @returns {Promise<string>}
   */
  public async getName(
    caller: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<string> {
    return await getName({
      token: await loadContractWithCaller(caller, this.token),
    });
  }

  /**
   * Returns Symbol of the token.
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<string>}
   */
  public async getSymbol(
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<string> {
    return await getSymbol({
      token: await loadContractWithCaller(blockchainOptions, this.token),
    });
  }

  /**
   * Returns version of the token smart contracts.
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<string>}
   */
  public async getVersion(
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<string> {
    return await getVersion({
      token: await loadContractWithCaller(blockchainOptions, this.token),
    });
  }

  /**
   * Returns the OnchainID of the token smart contracts.
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<string>}
   */
  public async getTokenOnchainID(
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<string> {
    return await getOnchainID({
      token: await loadContractWithCaller(blockchainOptions, this.token),
    });
  }

  /**
   * Returns the balance of tokens held by the address.
   * @param {string} address
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<BigNumber>}
   */
  @addressesFormat()
  public async getBalance(
    @required
    address: string,
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<BigNumber> {
    return await getBalance({
      address,
      token: await loadContractWithCaller(blockchainOptions, this.token),
    });
  }

  /**
   * Returns decimals of the Token.
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<number>}
   */
  public async getDecimals(
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<number> {
    return await getDecimals({
      token: await loadContractWithCaller(blockchainOptions, this.token),
    });
  }

  /**
   * Mints an amount of tokens into the address's wallet.
   * @param {string} address
   * @param {BigNumber} amount
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<TransactionResponse>}
   */
  @addressesFormat()
  public async mintToWallet(
    @required
    {
      address,
      amount,
    }: {
      address: string;
      amount: BigNumber;
    },
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<TransactionResponse> {
    return await mint({
      address,
      amount,
      token: await loadContractWithCaller(blockchainOptions, this.token, Role.TokenAgent, this),
      overrides: blockchainOptions.overrides ?? this._overrides,
    });
  }

  /**
   * Mints amounts of tokens to multiple addresses
   * @param {string[]} addresses
   * @param {BigNumber[]} amounts
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<TransactionResponse>}
   */
  @addressesFormat()
  public async batchMint(
    @required
    {
      addresses,
      amounts,
    }: {
      addresses: string[];
      amounts: BigNumber[];
    },
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<TransactionResponse> {
    return await batchMint({
      addresses,
      amounts,
      token: await loadContractWithCaller(blockchainOptions, this.token, Role.TokenAgent, this),
      overrides: blockchainOptions.overrides ?? this._overrides,
    });
  }

  /**
   * Burns an amount of token from the address's wallet.
   * @param {string} address
   * @param {BigNumber} amount
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<TransactionResponse>}
   */
  @addressesFormat()
  public async burnFromWallet(
    @required
    {
      address,
      amount,
    }: {
      address: string;
      amount: BigNumber;
    },
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<TransactionResponse> {
    return await burn({
      address,
      amount,
      token: await loadContractWithCaller(blockchainOptions, this.token, Role.TokenAgent, this),
      signer: this.signer,
      overrides: blockchainOptions.overrides ?? this._overrides,
    });
  }

  /**
   * Burns amounts of tokens from multiple addresses
   * @param {string[]} addresses
   * @param {BigNumber[]} amounts
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<TransactionResponse>}
   */
  @addressesFormat()
  public async batchBurn(
    @required
    {
      addresses,
      amounts,
    }: {
      addresses: string[];
      amounts: BigNumber[];
    },
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<TransactionResponse> {
    return await batchBurn({
      addresses,
      amounts,
      token: await loadContractWithCaller(blockchainOptions, this.token, Role.TokenAgent, this),
      overrides: blockchainOptions.overrides ?? this._overrides,
    });
  }

  /**
   * Freezes an address making it impossible to do operations like transfers.
   * @param {string} address
   * @param {boolean} freezeStatus
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<TransactionResponse>}
   */
  @addressesFormat()
  public async freezeAddress(
    @required
    {
      address,
      freezeStatus,
    }: {
      address: string;
      freezeStatus: boolean;
    },
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<TransactionResponse> {
    return await freezeAddress({
      address,
      freezeStatus,
      token: await loadContractWithCaller(blockchainOptions, this.token, Role.TokenAgent, this),
      signer: this.signer,
      overrides: blockchainOptions.overrides ?? this._overrides,
    });
  }

  /**
   * Freezes addresses in Batch
   * @param {string[]} addresses
   * @param {boolean[]} freezeStatus
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<TransactionResponse>}
   */
  @addressesFormat()
  public async batchFreezeAddresses(
    @required
    {
      addresses,
      freezeStatus,
    }: {
      addresses: string[];
      freezeStatus: boolean[];
    },
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<TransactionResponse> {
    return await batchFreezeAddresses({
      addresses,
      freezeStatus,
      token: await loadContractWithCaller(blockchainOptions, this.token, Role.TokenAgent, this),
      signer: this.signer,
      overrides: blockchainOptions.overrides ?? this._overrides,
    });
  }

  /**
   * Freezes a portion of tokens held by the address's wallet.
   * @param {string} address
   * @param {BigNumber} amount
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<TransactionResponse>}
   */
  @addressesFormat()
  public async freezePartialTokens(
    @required
    {
      address,
      amount,
    }: {
      address: string;
      amount: BigNumber;
    },
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<TransactionResponse> {
    return await freezePartialTokens({
      address,
      amount,
      token: await loadContractWithCaller(blockchainOptions, this.token, Role.TokenAgent, this),
      signer: this.signer,
      overrides: blockchainOptions.overrides ?? this._overrides,
    });
  }

  /**
   * BatchFreezes a portion of tokens held by a list of shareholders
   * @param {string[]} addresses
   * @param {BigNumber[]} amounts
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<TransactionResponse>}
   */
  @addressesFormat()
  public async batchFreezePartialTokens(
    @required
    {
      addresses,
      amounts,
    }: {
      addresses: string[];
      amounts: BigNumber[];
    },
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<TransactionResponse> {
    return await batchFreezePartialTokens({
      addresses,
      amounts,
      token: await loadContractWithCaller(blockchainOptions, this.token, Role.TokenAgent, this),
      signer: this.signer,
      overrides: blockchainOptions.overrides ?? this._overrides,
    });
  }

  /**
   * Unfreezes a portion of tokens held by the address's wallet.
   * @param {string} address
   * @param {BigNumber} amount
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<TransactionResponse>}
   */
  @addressesFormat()
  public async unfreezePartialTokens(
    @required
    {
      address,
      amount,
    }: {
      address: string;
      amount: BigNumber;
    },
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<TransactionResponse> {
    return await unfreezePartialTokens({
      address,
      amount,
      token: await loadContractWithCaller(blockchainOptions, this.token, Role.TokenAgent, this),
      signer: this.signer,
      overrides: blockchainOptions.overrides ?? this._overrides,
    });
  }

  /**
   * BatchUnfreezes a portion of tokens held by a list of shareholders
   * @param {string[]} addresses
   * @param {BigNumber[]} amounts
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<TransactionResponse>}
   */
  @addressesFormat()
  public async batchUnfreezePartialTokens(
    @required
    {
      addresses,
      amounts,
    }: {
      addresses: string[];
      amounts: BigNumber[];
    },
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<TransactionResponse> {
    return await batchUnfreezePartialTokens({
      addresses,
      amounts,
      token: await loadContractWithCaller(blockchainOptions, this.token, Role.TokenAgent, this),
      signer: this.signer,
      overrides: blockchainOptions.overrides ?? this._overrides,
    });
  }

  /**
   * @dev recovery function used to force transfer tokens from a lost wallet to a new wallet for an investor.
   * This function can only be called by a wallet set as agent of the token
   * @param lostWallet
   * @param newWallet
   * @param onchainID
   * @param blockchainOptions
   */
  public async recoveryAddress(
    {
      lostWallet,
      newWallet,
      onchainID,
    }: {
      lostWallet: string;
      newWallet: string;
      onchainID: string;
    },
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<TransactionResponse> {
    return await recoveryAddress({
      lostWallet,
      newWallet,
      onchainID,
      token: await loadContractWithCaller(blockchainOptions, this.token),
      signer: this.signer,
      overrides: blockchainOptions.overrides ?? this._overrides,
    });
  }

  /**
   * Transfers an amount of tokens to the address's wallet.
   * @param {string} to
   * @param {BigNumber} amount
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<TransactionResponse>}
   */
  @addressesFormat()
  public async transfer(
    @required
    {
      to,
      amount,
    }: {
      to: string;
      amount: BigNumber;
    },
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<TransactionResponse> {
    return await transfer({
      to,
      amount,
      token: await loadContractWithCaller(blockchainOptions, this.token),
      signer: this.signer,
      overrides: blockchainOptions.overrides ?? this._overrides,
    });
  }

  /**
   * Transfer between two addresses.
   * @param {string} from
   * @param {string} to
   * @param {BigNumber} amount
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<TransactionResponse>}
   */
  @addressesFormat()
  public async transferFrom(
    @required
    {
      from,
      to,
      amount,
    }: {
      from: string;
      to: string;
      amount: BigNumber;
    },
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<TransactionResponse> {
    return await transferFrom({
      from,
      to,
      amount,
      token: await loadContractWithCaller(blockchainOptions, this.token),
      signer: this.signer,
      overrides: blockchainOptions.overrides ?? this._overrides,
    });
  }

  /**
   * Conditional Transfer between two addresses.
   * @param {string} from
   * @param {string} to
   * @param {BigNumber} amount
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<TransactionResponse>}
   */
  @addressesFormat()
  public async approveAndTransfer(
    @required
    {
      from,
      to,
      amount,
    }: {
      from: string;
      to: string;
      amount: BigNumber;
    },
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<TransactionResponse> {
    const complianceContract = new Contract(
      this.compliance.address,
      [
        {
          inputs: [
            {
              internalType: 'address',
              name: '_from',
              type: 'address',
            },
            {
              internalType: 'address',
              name: '_to',
              type: 'address',
            },
            {
              internalType: 'uint256',
              name: '_amount',
              type: 'uint256',
            },
          ],
          name: 'approveAndTransfer',
          outputs: [],
          stateMutability: 'nonpayable',
          type: 'function',
        },
      ],
      blockchainOptions.signer || blockchainOptions.provider,
    );
    return await complianceContract.approveAndTransfer(from, to, amount);
  }

  /**
   * Sets 'amount' as the allowance of 'spender' over the caller's token
   * @param address
   * @param amount
   * @param blockchainOptions
   */
  @addressesFormat()
  public async approve(
    @required
    {
      address,
      amount,
    }: {
      address: string;
      amount: BigNumber;
    },
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<TransactionResponse> {
    return await approve({
      address,
      amount,
      token: await loadContractWithCaller(blockchainOptions, this.token),
      signer: this.signer,
      overrides: blockchainOptions.overrides ?? this._overrides,
    });
  }

  /**
   * Returns the amount of tokens that 'spender' is allowed to spend on behalf the 'owner'
   * @param owner
   * @param spender
   * @param blockchainOptions
   */
  public async getAllowance(
    {
      owner,
      spender,
    }: {
      owner: string;
      spender: string;
    },
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<BigNumber> {
    return await getAllowance({
      owner,
      spender,
      token: await loadContractWithCaller(blockchainOptions, this.token),
      signer: this.signer,
      overrides: blockchainOptions.overrides ?? this._overrides,
    });
  }

  /**
   * Increases the allowance granted to 'spender' by the caller
   * @param address
   * @param amount
   * @param blockchainOptions
   */
  @addressesFormat()
  public async increaseAllowance(
    @required
    {
      address,
      amount,
    }: {
      address: string;
      amount: BigNumber;
    },
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<TransactionResponse> {
    return await increaseAllowance({
      address,
      amount,
      token: await loadContractWithCaller(blockchainOptions, this.token),
      signer: this.signer,
      overrides: blockchainOptions.overrides ?? this._overrides,
    });
  }

  /**
   * Decreases the allowance granted to 'spender' by the caller
   * @param address
   * @param amount
   * @param blockchainOptions
   */
  @addressesFormat()
  public async decreaseAllowance(
    @required
    {
      address,
      amount,
    }: {
      address: string;
      amount: BigNumber;
    },
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<TransactionResponse> {
    return await decreaseAllowance({
      address,
      amount,
      token: await loadContractWithCaller(blockchainOptions, this.token),
      signer: this.signer,
      overrides: blockchainOptions.overrides ?? this._overrides,
    });
  }

  /**
   * Transfers an amount of tokens to a list of addresses in Batch
   * @param {string[]} toList
   * @param {BigNumber[]} amounts
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<TransactionResponse>}
   */
  @addressesFormat()
  public async batchTransfer(
    @required
    {
      toList,
      amounts,
    }: {
      toList: string[];
      amounts: BigNumber[];
    },
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<TransactionResponse> {
    return await batchTransfer({
      toList,
      amounts,
      token: await loadContractWithCaller(blockchainOptions, this.token),
      signer: this.signer,
      overrides: blockchainOptions.overrides ?? this._overrides,
    });
  }

  /**
   * Enforces the execution of a transfer between two addresses.
   * @param {string} from
   * @param {string} to
   * @param {BigNumber} amount
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<TransactionResponse>}
   */
  @addressesFormat()
  public async forceTransfer(
    @required
    {
      from,
      to,
      amount,
    }: {
      from: string;
      to: string;
      amount: BigNumber;
    },
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<TransactionResponse> {
    return await forceTransfer({
      from,
      to,
      amount,
      token: await loadContractWithCaller(blockchainOptions, this.token, Role.TokenAgent, this),
      signer: this.signer,
      overrides: blockchainOptions.overrides ?? this._overrides,
    });
  }

  /**
   * Enforces the execution of a transfer between two addresses in Batch.
   * @param {string[]} fromList
   * @param {string[]} toList
   * @param {BigNumber[]} amounts
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<TransactionResponse>}
   */
  @addressesFormat()
  public async batchForceTransfer(
    @required
    {
      fromList,
      toList,
      amounts,
    }: {
      fromList: string[];
      toList: string[];
      amounts: BigNumber[];
    },
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<TransactionResponse> {
    return await batchForceTransfer({
      fromList,
      toList,
      amounts,
      token: await loadContractWithCaller(blockchainOptions, this.token, Role.TokenAgent, this),
      signer: this.signer,
      overrides: blockchainOptions.overrides ?? this._overrides,
    });
  }

  /**
   * Returns the amount of frozen tokens of held by the address's wallet.
   * @param {string} address
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<BigNumber>}
   */
  @addressesFormat()
  public async getFrozenAmount(
    @required address: string,
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<BigNumber> {
    return getFrozenAmount({
      address,
      token: await loadContractWithCaller(blockchainOptions, this.token),
    });
  }

  /**
   * Returns freeze Status on address
   * @param {string} address
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<BigNumber>}
   */
  @addressesFormat()
  public async isAddressFrozen(
    @required
    address: string,
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<boolean> {
    return isFrozen({
      address,
      token: await loadContractWithCaller(blockchainOptions, this.token),
    });
  }

  /**
   * Changes token's name
   * /!\ Use it carefully, could lead to undefined behavior on Etherscan.
   * @param {string} name
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<TransactionResponse>}
   */
  public async setName(
    name: string,
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
      overrides: this.overrides,
    },
  ): Promise<TransactionResponse> {
    blockchainOptions = getTransactionOptions(this, blockchainOptions);

    return await setName({
      name,
      token: await loadContractWithCaller(blockchainOptions, this.token, Role.TokenOwner, this),
      overrides: blockchainOptions.overrides ?? {},
    });
  }

  /**
   * Changes token's symbol
   * /!\ Use it carefully, could lead to undefined behavior on Etherscan.
   * @param {string} symbol
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<TransactionResponse>}
   */
  public async setSymbol(
    symbol: string,
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
      overrides: this.overrides,
    },
  ): Promise<TransactionResponse> {
    blockchainOptions = getTransactionOptions(this, blockchainOptions);
    return await setSymbol({
      symbol,
      token: await loadContractWithCaller(blockchainOptions, this.token, Role.TokenOwner, this),
      overrides: blockchainOptions.overrides ?? {},
    });
  }

  /**
   * Changes token's OnchainID
   * /!\ Use it carefully, could lead to undefined behavior on Etherscan.
   * @param {string} onchainID
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<TransactionResponse>}
   */
  public async setOnchainID(
    onchainID: string,
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
      overrides: this.overrides,
    },
  ): Promise<TransactionResponse> {
    blockchainOptions = getTransactionOptions(this, blockchainOptions);
    return await setOnchainID({
      onchainID,
      token: await loadContractWithCaller(blockchainOptions, this.token, Role.TokenOwner, this),
      overrides: blockchainOptions.overrides ?? {},
    });
  }

  /**
   * Pause Token smart contract, rendering execution of operations impossible.
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<TransactionResponse>}
   */
  public async pauseToken(
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<TransactionResponse> {
    blockchainOptions = getTransactionOptions(this, blockchainOptions);
    return await pauseToken({
      token: await loadContractWithCaller(blockchainOptions, this.token, Role.TokenAgent, this),
      signer: this.signer,
      overrides: blockchainOptions.overrides ?? this._overrides,
    });
  }

  /**
   * Unpause Token smart contract, removes all restrictions imposed previously.
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<TransactionResponse>}
   */
  public async unpauseToken(
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<TransactionResponse> {
    blockchainOptions = getTransactionOptions(this, blockchainOptions);

    return await unpauseToken({
      token: await loadContractWithCaller(blockchainOptions, this.token, Role.TokenAgent, this),
      signer: this.signer,
      overrides: blockchainOptions.overrides ?? this._overrides,
    });
  }

  /**
   * Checks if a transfer is Compliant with Compliance policy
   * @param {{from: string; to: string; amount: number}} tx
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<boolean>}
   */
  @addressesFormat()
  public async checkTransferCompliance(
    @required
    tx: {
      from: string;
      to: string;
      amount: number;
    },
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<boolean> {
    return (await loadContractWithCaller(blockchainOptions, this.compliance)).canTransfer(tx.from, tx.to, tx.amount);
  }

  /**
   * Returns Pause status.
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<boolean>}
   */
  public async getPauseStatus(
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<boolean> {
    return await getPauseStatus({
      token: await loadContractWithCaller(blockchainOptions, this.token),
    });
  }
}
